import { useLocation } from "@remix-run/react";
import { motion } from "framer-motion";
import { CalendarClock } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "~/components/ui/popover";
import { cn } from "~/lib/utils";
import {
  useBookingCartStore,
  useVoucherBookingCartStore,
} from "~/stores/bookingCart";

interface BookingCartIconProps {
  className?: string;
}

export const BookingCartIcon = ({ className }: BookingCartIconProps = {}) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  // Decidiamo quale store leggere in base alla url: il navbar è fuori dal
  // provider del booking, quindi scegliamo a runtime.
  const isVoucherFlow =
    location.pathname.includes("/booking-voucher") ||
    location.pathname.includes("/redeem-voucher");
  const normalCart = useBookingCartStore();
  const voucherCart = useVoucherBookingCartStore();
  const { bookingCart, setMobileCartOpen } = isVoucherFlow
    ? voucherCart
    : normalCart;
  const [itemCount, setItemCount] = useState(0);
  const [isClient, setIsClient] = useState(false);
  const [shouldBounce, setShouldBounce] = useState(false);
  const [open, setOpen] = useState(false);
  const prevItemCountRef = useRef(0);
  const isInitialMountRef = useRef(true);
  const iconRef = useRef<HTMLDivElement>(null);

  const isBookingPage = location.pathname.includes("/booking");

  const items =
    bookingCart?.BasketItems?.filter((item: any) => item.Role !== "INCLUDED") ||
    [];

  useEffect(() => {
    setIsClient(true);
    const newCount = items.length;

    if (isInitialMountRef.current) {
      prevItemCountRef.current = newCount;
      setItemCount(newCount);
      isInitialMountRef.current = false;
      return;
    }

    if (newCount > prevItemCountRef.current) {
      if (prevItemCountRef.current === 0) {
        setOpen(true);
        setTimeout(() => setOpen(false), 5000);
      } else {
        setShouldBounce(true);
        setTimeout(() => setShouldBounce(false), 600);
        setOpen(true);
        setTimeout(() => setOpen(false), 5000);
      }
    }

    prevItemCountRef.current = newCount;
    setItemCount(newCount);
  }, [items.length]);

  const summary = items.reduce(
    (acc: { total: number; currency: string }, item: any) => {
      if (item.Role !== "INCLUDED") {
        acc.total += item.GrossAmount?.Price || 0;
        acc.currency = item.GrossAmount?.Currency || "";
      }
      return acc;
    },
    { total: 0, currency: "" }
  );

  const handleClick = () => {
    if (isBookingPage) {
      setMobileCartOpen(true);
      setOpen(false);
    } else {
      setOpen(!open);
    }
  };

  return (
    <div ref={iconRef} className="relative">
      {!isBookingPage && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverAnchor asChild>
            <button
              type="button"
              onClick={handleClick}
              className={cn(
                "relative p-2 transition-colors block",
                className || "text-secondary"
              )}
            >
              <motion.div
                className="w-5 h-5"
                animate={
                  shouldBounce
                    ? {
                        scale: [1, 1.2, 1],
                      }
                    : {}
                }
                transition={{
                  duration: 0.5,
                  ease: "easeOut",
                }}
              >
                <CalendarClock size={20} />
              </motion.div>

              {isClient && itemCount > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    type: "spring",
                    stiffness: 500,
                    damping: 30,
                  }}
                  className="absolute -top-0 -right-0 bg-[#FF3838] text-white text-xs rounded-full h-4 w-4 flex items-center justify-center"
                >
                  {itemCount}
                </motion.span>
              )}
            </button>
          </PopoverAnchor>
          <PopoverContent
            align="end"
            sideOffset={8}
            className="w-72 p-0 bg-[#f7ece3]"
          >
            {items.length === 0 ? (
              <div className="p-6 text-center">
                <p className="text-gray-700">{t("Carrello vuoto")}</p>
              </div>
            ) : (
              <>
                <div className="max-h-[300px] overflow-y-auto grid grid-cols-1 gap-2 pt-4">
                  {items.map((item: any, index: number) => {
                    const serviceName = item.Service || t("Servizio");
                    const date = item.CheckinDate
                      ? new Date(item.CheckinDate).toLocaleDateString(
                          i18n.language,
                          {
                            day: "2-digit",
                            month: "short",
                          }
                        )
                      : "";
                    const time = item.StartTime
                      ? item.StartTime.slice(0, 5)
                      : "";
                    const price = item.GrossAmount?.Price || 0;

                    return (
                      <div
                        key={`${item.Service}-${item.LineNr}-${index}`}
                        className="flex px-4"
                      >
                        <div className="flex-1 flex flex-col justify-between">
                          <div className="text-secondary font-medium text-sm leading-tight mb-1">
                            {serviceName}
                          </div>
                          <div className="text-gray-600 text-xs mb-1">
                            {date} {time && `- ${time}`}
                          </div>
                          <div className="text-black text-sm mb-2">
                            {price.toFixed(2).replace(".", ",")} €
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="p-4 pt-2 border-t border-gray-200">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-semibold">
                      {t("Totale (IVA inclusa)")}:
                    </span>
                    <span className="text-sm font-semibold">
                      {summary.total.toFixed(2).replace(".", ",")} €
                    </span>
                  </div>
                </div>
              </>
            )}
          </PopoverContent>
        </Popover>
      )}
      {isBookingPage && (
        <button
          type="button"
          onClick={handleClick}
          className={cn(
            "relative p-2 transition-colors block",
            className || "text-secondary"
          )}
        >
          <motion.div
            className="w-5 h-5"
            animate={
              shouldBounce
                ? {
                    scale: [1, 1.2, 1],
                  }
                : {}
            }
            transition={{
              duration: 0.5,
              ease: "easeOut",
            }}
          >
            <CalendarClock size={20} />
          </motion.div>

          {isClient && itemCount > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{
                type: "spring",
                stiffness: 500,
                damping: 30,
              }}
              className="absolute -top-0 -right-0 bg-[#FF3838] text-white text-xs rounded-full h-4 w-4 flex items-center justify-center"
            >
              {itemCount}
            </motion.span>
          )}
        </button>
      )}
    </div>
  );
};
